import React, { useEffect, useRef } from 'react';
import { AccessibilityInfo, Animated, Platform, Pressable, ScrollView, useWindowDimensions, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Text from '@/components/Typography/Text';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';

export default function BotSetupSkeleton({ onBack }) {
  const { colors, theme } = useTheme();
  const { width, height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const compact = width < 600 || height < 800;
  const dark = isDarkTheme(theme);
  const opacity = useRef(new Animated.Value(0.55)).current;
  useEffect(() => {
    let active = true;
    let animation;
    const update = reduced => {
      animation?.stop();
      if (!active) return;
      if (reduced) { opacity.setValue(0.7); return; }
      animation = Animated.loop(Animated.sequence([
        Animated.timing(opacity, { toValue: 1, duration: 900, useNativeDriver: Platform.OS !== 'web' }),
        Animated.timing(opacity, { toValue: 0.45, duration: 900, useNativeDriver: Platform.OS !== 'web' }),
      ]));
      animation.start();
    };
    AccessibilityInfo.isReduceMotionEnabled().then(update);
    const listener = AccessibilityInfo.addEventListener('reduceMotionChanged', update);
    return () => { active = false; animation?.stop(); listener.remove(); };
  }, [opacity]);
  const accent = '#8b7cf8';
  const bar = (barWidth, barHeight = 12) => <View style={{ width: barWidth, height: barHeight, borderRadius: 6, backgroundColor: dark ? '#494355' : '#d5cfe3' }} />;
  return <View style={{ flex: 1, paddingTop: insets.top }}>
    <View style={{ paddingHorizontal: compact ? 16 : 24, paddingVertical: compact ? 4 : 16 }}>
      <Pressable onPress={onBack} accessibilityRole="button" accessibilityLabel="Back to bots" style={{ alignSelf: 'flex-start', minHeight: 44, justifyContent: 'center', paddingHorizontal: 4 }}>
        <Text size="s" style={{ color: accent }}>← Back to bots</Text>
      </Pressable>
    </View>
    <ScrollView contentContainerStyle={{ width: '100%', maxWidth: 760, alignSelf: 'center', padding: compact ? 16 : 24, paddingTop: compact ? 8 : 24, gap: compact ? 20 : 28 }}>
      <View style={{ flexDirection: 'row', gap: 6 }}>
        {['Name', 'Purpose', 'Instructions', 'Avatar', 'Review'].map((label, index) => <View key={label} style={{ flex: 1, gap: 9 }}>
          <View style={{ height: 3, borderRadius: 3, backgroundColor: index === 0 ? accent : colors.border_color_secondary }} />
          <Text size="xxs" color={index === 0 ? 'primary' : 'secondary'}>{label}</Text>
        </View>)}
      </View>
      <View style={{ gap: 8 }}>
        <Text weight="bold" style={{ fontSize: compact ? 24 : 30, lineHeight: compact ? 30 : 38 }}>Who are we creating?</Text>
        <Text color="secondary" style={{ lineHeight: 22 }}>A little inspiration for your next teammate.</Text>
      </View>
      <View style={{ borderRadius: 22, padding: compact ? 18 : 24, gap: 22, borderWidth: 1, borderColor: dark ? '#383047' : '#e1daee', backgroundColor: colors.solid_color_secondary }} accessibilityLabel="Preparing bot suggestions" accessibilityState={{ busy: true }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <View style={{ width: 42, height: 42, borderRadius: 14, backgroundColor: dark ? '#292344' : '#eee9ff', alignItems: 'center', justifyContent: 'center' }}><Text style={{ color: accent, fontSize: 24 }}>✦</Text></View>
          <View style={{ flex: 1, gap: 4 }}><Text weight="bold">Arya is finding a few ideas</Text><Text size="s" color="secondary">Turning possibilities into a starting point…</Text></View>
        </View>
        <Animated.View style={{ opacity, gap: 10 }} accessibilityElementsHidden importantForAccessibility="no-hide-descendants" aria-hidden>
          {[['58%', '78%'], ['71%', '61%'], ['48%', '85%']].map(([titleWidth, detailWidth], index) => <View key={index} style={{ minHeight: 74, borderRadius: 12, padding: 16, flexDirection: 'row', alignItems: 'center', gap: 14, borderWidth: 1, borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_primary }}>
            <View style={{ width: 26, height: 26, borderRadius: 7, borderWidth: 1, borderColor: dark ? '#494355' : '#d5cfe3' }} />
            <View style={{ flex: 1, gap: 10 }}>{bar(titleWidth, 14)}{bar(detailWidth, 8)}</View>
          </View>)}
        </Animated.View>
        <Text size="s" color="secondary">Pick an idea or give your bot a name of your own.</Text>
      </View>
    </ScrollView>
  </View>;
}
